import { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import type { HistoricalMwrrItem } from '@/api/types';

interface ReturnsTableProps {
    /** Items to display - MWRR items */
    items: HistoricalMwrrItem[];
}

type SortDirection = 'asc' | 'desc';
type SortField = 'period' | 'return';

/**
 * Table component for displaying historical MWRR data.
 *
 * Features:
 * - Sortable by period or return value
 * - Color-coded positive/negative returns
 * - Handles null values with "-" placeholder
 */
export function ReturnsTable({ items }: ReturnsTableProps) {
    const [sortField, setSortField] = useState<SortField>('period');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    // Get the return value from an item
    const getReturnValue = (item: HistoricalMwrrItem): number | null => {
        return item.mwrr;
    };

    const sortedItems = useMemo(() => {
        const sorted = [...items];
        sorted.sort((a, b) => {
            let comparison = 0;

            if (sortField === 'period') {
                comparison = new Date(a.periodEnd).getTime() - new Date(b.periodEnd).getTime();
            } else if (sortField === 'return') {
                const aVal = getReturnValue(a) ?? -Infinity;
                const bVal = getReturnValue(b) ?? -Infinity;
                comparison = aVal - bVal;
            }

            return sortDirection === 'asc' ? comparison : -comparison;
        });
        return sorted;
    }, [items, sortField, sortDirection]);

    /**
     * Render sort icon showing current sort state.
     */
    function SortIcon({ field }: { field: SortField }) {
        if (field !== sortField) {
            return (
                <span className="ml-1 inline-flex flex-col opacity-30">
                    <ChevronUpIcon className="h-3 w-3 -mb-1" />
                    <ChevronDownIcon className="h-3 w-3" />
                </span>
            );
        }
        return sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-4 w-4 ml-1 inline" />
        ) : (
            <ChevronDownIcon className="h-4 w-4 ml-1 inline" />
        );
    }

    /**
     * Format percentage with color coding.
     */
    function formatPercent(value: number | null): React.ReactNode {
        if (value === null) {
            return <span className="text-gray-400 dark:text-gray-500">-</span>;
        }

        const formatted = `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
        const colorClass = value >= 0
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400';

        return <span className={colorClass}>{formatted}</span>;
    }

    // Get the column header label
    const columnLabel = 'MWRR';

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                    <tr>
                        <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-800"
                            onClick={() => handleSort('period')}
                        >
                            Period
                            <SortIcon field="period" />
                        </th>
                        <th
                            scope="col"
                            className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-800"
                            onClick={() => handleSort('return')}
                        >
                            {columnLabel}
                            <SortIcon field="return" />
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedItems.map((item, index) => (
                        <tr
                            key={item.periodEnd}
                            className={index % 2 === 0 ? 'bg-white dark:bg-secondary' : 'bg-gray-50 dark:bg-dark'}
                        >
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                                {item.periodLabel}
                            </td>
                            <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                                {formatPercent(getReturnValue(item))}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {items.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No return data available
                </div>
            )}
        </div>
    );
}
